import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import Footer from '../components/Footer';
import { FaEye, FaEyeSlash, FaCamera, FaFileAlt } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';

function ProfessionalProfile() {
  const navigate = useNavigate();

  // Form state - starts empty, populated from API
  const [formData, setFormData] = useState({
    professionalId: null,
    profession: '',
    name: '',
    address: '',
    gender: '',
    dateOfBirth: '',
    pincode: '',
    qualification: '',
    specialization: '',
    registrationNumber: '',
    experience: '',
    experienceDetails: '',
    mobileNumber: '',
    emailId: '',
    password: '',
    reenterPassword: ''
  });

  // Existing files from API (for display + download)
  const [existingFiles, setExistingFiles] = useState({
    cvName: '',
    cvBytes: '',
    certName: '',
    certBytes: ''
  });
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [isQualificationDisabled, setIsQualificationDisabled] = useState(true);
  const [location, setLocation] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialContact, setInitialContact] = useState({ emailId: '', mobileNumber: '' });

  // New files (user selects)
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [registrationCertificateFile, setRegistrationCertificateFile] = useState(null);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const downloadBase64File = (base64, filename = 'file') => {
    try {
      if (!base64) return;

      // If API sends pure base64 without prefix, we convert to blob
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Try to infer type from filename
      const lower = (filename || '').toLowerCase();
      let mime = 'application/octet-stream';
      if (lower.endsWith('.pdf')) mime = 'application/pdf';
      else if (lower.endsWith('.doc')) mime = 'application/msword';
      else if (lower.endsWith('.docx')) mime =
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) mime = 'image/jpeg';
      else if (lower.endsWith('.png')) mime = 'image/png';

      const blob = new Blob([byteArray], { type: mime });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Unable to download file.');
    }
  };

  const openBase64InNewTab = (base64, filename = 'file') => {
    try {
      if (!base64) return;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const lower = (filename || '').toLowerCase();
      let mime = 'application/octet-stream';
      if (lower.endsWith('.pdf')) mime = 'application/pdf';
      else if (lower.endsWith('.doc')) mime = 'application/msword';
      else if (lower.endsWith('.docx'))
        mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) mime = 'image/jpeg';
      else if (lower.endsWith('.png')) mime = 'image/png';

      const blob = new Blob([byteArray], { type: mime });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // leave url revocation to browser window lifecycle to avoid revoke before load
    } catch {
      toast.error('Unable to view file.');
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Pincode numeric-only, reset district/location + errors
    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      setPincodeError('');
      setLocation('');
      setIdDistrict(null);
      return;
    }

    if (name === "password" || name === "reenterPassword") {
      if (value.length > 20) {
        toast.error("Password cannot exceed 20 characters!");
        return;
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    // Mobile numeric-only
    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, mobileNumber: numericValue }));
      setMobileError('');
      return;
    }

    if (name === 'emailId') {
      setFormData((prev) => ({ ...prev, emailId: value }));
      setEmailError('');
      return;
    }

    // Experience clamp 0–70
    if (name === 'experience') {
      let numValue = parseInt(value, 10);
      if (Number.isNaN(numValue) || numValue < 0) {
        setFormData((prev) => ({ ...prev, [name]: '' }));
        return;
      }
      if (numValue > 70) numValue = 70;
      setFormData((prev) => ({ ...prev, [name]: String(numValue) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle rich text editor changes
  const handleExperienceDetailsChange = (value) => {
    const textContent = value.replace(/<[^>]*>/g, '');
    if (textContent.length <= 8000) {
      setFormData((prev) => ({ ...prev, experienceDetails: value }));
    }
  };

  // Handle file uploads
  const handleFileChange = (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File size validation (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`${fileType} file size must be less than 5MB!`);
      e.target.value = '';
      return;
    }

    // File type validation
    let allowedTypes = [];
    if (fileType === 'CV') {
      allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error('CV must be a PDF, Word document, or image (.pdf, .doc, .docx, .jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
      setCvFile(file);
      return;
    }

    if (fileType === 'Photo') {
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Photo must be a JPEG or PNG image (.jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
      setPhotoFile(file);

      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
      return;
    }

    // Registration Certificate (same as your Admin screen)
    if (fileType === 'Registration Certificate') {
      allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Registration Certificate must be a PDF, Word document, or image (.pdf, .doc, .docx, .jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
      setRegistrationCertificateFile(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  // Character counters
  const addressCount = formData.address.length;
  const experienceCount = (formData.experienceDetails || '').replace(/<[^>]*>/g, '').length;

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [typesRes, degreesRes, profileRes] = await Promise.all([
        client.get('/api/professional-types'),
        client.get('/api/professional-degrees'),
        client.get('/api/healthcare-professionals/current')
      ]);

      const typeList = typesRes.data?.data || [];
      setProfessions(typeList);

      const degreeList = degreesRes.data?.data || [];
      setDegrees(degreeList);

      const profile = profileRes.data?.data;

      if (profile) {
        // bind files from API
        setExistingFiles({
          cvName: profile.bioDataFileName || '',
          cvBytes: profile.bioDataFileBytes || '',
          certName: profile.registrationCertificateFileName || '',
          certBytes: profile.registrationCertificateFileBytes || ''
        });

        // bind photo preview from API
        const apiPhotoSrc = profile.photoFileBytes
          ? `data:image/jpeg;base64,${profile.photoFileBytes}`
          : null;
        setPhotoPreview(apiPhotoSrc);

        setFormData((prev) => ({
          ...prev,
          professionalId: profile.professionalID ?? null,
          profession: profile.idProfessionType ? String(profile.idProfessionType) : '',
          name: profile.professionalName || '',
          address: profile.address || '',
          gender: profile.gender?.toLowerCase() || '',
          dateOfBirth: formatDateForInput(profile.dateOfBirth),
          pincode: profile.pincode || '',
          qualification: profile.idProfessionalDegree ? String(profile.idProfessionalDegree) : '',
          specialization: profile.specialization || '',
          registrationNumber: profile.registrationNumber || '',
          experience: profile.yearsOfExperience != null ? String(profile.yearsOfExperience) : '',
          experienceDetails: profile.experienceDetails || '',
          mobileNumber: profile.mobileNumber || '',
          emailId: profile.emailID || profile.emailId || '',
          password: '',
          reenterPassword: ''
        }));

        setInitialContact({
          emailId: profile.emailID || profile.emailId || '',
          mobileNumber: profile.mobileNumber || ''
        });

        // district name mapping safe
        setLocation(
          profile.district?.districtName ||
          profile.district?.distrinctName ||
          profile.districtName ||
          ''
        );
        setIdDistrict(profile.idDistrict ?? null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update qualification options when profession changes
  useEffect(() => {
    const professionId = parseInt(formData.profession, 10);
    if (Number.isFinite(professionId)) {
      const filtered = degrees
        .filter((deg) => deg.idProfessional === professionId)
        .map((deg) => ({ id: deg.idProfessionalDegree, name: deg.professionalDegreeName }))
        .filter((deg) => deg.id && deg.name);

      setQualificationOptions(filtered);
      setIsQualificationDisabled(filtered.length === 0);

      if (!filtered.find((deg) => String(deg.id) === formData.qualification)) {
        setFormData((prev) => ({ ...prev, qualification: '' }));
      }
    } else {
      setQualificationOptions([]);
      setIsQualificationDisabled(true);
      setFormData((prev) => ({ ...prev, qualification: '' }));
    }
  }, [degrees, formData.profession, formData.qualification]);

  // Validate pincode via API
  const validatePincode = async () => {
    let message = '';
    setPincodeError('');
    setLocation('');
    setIdDistrict(null);

    if ((formData.pincode || '').length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(`/api/districts/validate-pincode?pincode=${formData.pincode}`);
      const result = response.data?.data;
      
      if (result?.districtName) {
        setLocation(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      return { ok: false, message };
    } catch (error) {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  // Validate email/mobile via API (skip if unchanged)
  const validateContact = async (field = 'both') => {
    let message = '';

    if (field !== 'email') setMobileError('');
    if (field !== 'mobile') setEmailError('');

    const email = (formData.emailId || '').trim();
    const mobile = (formData.mobileNumber || '').trim();

    const emailValid = field === 'mobile' ? true : /^\S+@\S+\.\S+$/.test(email);
    const mobileValid = field === 'email' ? true : mobile.length === 10;

    if (!emailValid || !mobileValid) {
      if (!emailValid) {
        message = 'Enter a valid email.';
        setEmailError(message);
      }
      if (!mobileValid) {
        message = message || 'Enter a valid 10-digit mobile number.';
        setMobileError('Enter a valid 10-digit mobile number.');
      }
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    // ✅ Skip API call if unchanged
    const emailChanged =
      email.toLowerCase() !== (initialContact.emailId || '').trim().toLowerCase();
    const mobileChanged =
      mobile !== (initialContact.mobileNumber || '').trim();

    if (field === 'email' && !emailChanged) return { ok: true, message: '' };
    if (field === 'mobile' && !mobileChanged) return { ok: true, message: '' };
    if (field === 'both' && !emailChanged && !mobileChanged) return { ok: true, message: '' };

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : (email || null),
        mobileNumber: field === 'email' ? null : (mobile || null)
      };

      const res = await client.post('/api/users/validate-contact', payload);
      const exists = res.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
          setEmailError('Email already exists.');
          setMobileError('Mobile number already exists.');
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch (err) {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') setEmailError(message);
      else if (field === 'mobile') setMobileError(message);
      else {
        setEmailError(message);
        setMobileError(message);
      }
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };


  const passwordsMatch =
    formData.password === formData.reenterPassword && formData.reenterPassword !== '';

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !isEditMode) return;
    
    const pincodeResult = await validatePincode();

    const emailChanged =
      (formData.emailId || '').trim().toLowerCase() !== (initialContact.emailId || '').trim().toLowerCase();

    const mobileChanged =
      (formData.mobileNumber || '').trim() !== (initialContact.mobileNumber || '').trim();

    if (emailChanged) {
      const emailResult = await validateContact('email');
      if (!emailResult.ok) {
        toast.error(emailResult.message || 'Please enter a valid email.');
        return;
      }
    }

    if (mobileChanged) {
      const mobileResult = await validateContact('mobile');
      if (!mobileResult.ok) {
        toast.error(mobileResult.message || 'Please enter a valid mobile number.');
        return;
      }
    }

    // Password validation (optional)
    if (formData.password || formData.reenterPassword) {
      if (formData.password !== formData.reenterPassword) {
        toast.error('Passwords do not match!');
        return;
      }
      if (formData.password.length < 6 || formData.password.length > 20) {
        toast.error('Password must be between 6 and 20 characters!');
        return;
      }
    }

    const experience = parseInt(formData.experience, 10);
    if (!Number.isFinite(experience) || experience < 0 || experience > 70) {
      toast.error('Experience must be between 0 and 70 years!');
      return;
    }

    const experienceDetailsText = (formData.experienceDetails || '').replace(/<[^>]*>/g, '');
    if (experienceDetailsText.length > 8000) {
      toast.error('Experience details must not exceed 8000 characters!');
      return;
    }
    if (experienceDetailsText.trim().length === 0) {
      toast.error('Experience details is required!');
      return;
    }

    if (!formData.professionalId) {
      toast.error('Professional ID missing. Please reload and try again.');
      return;
    }

    if (!location || !idDistrict) {
      toast.error('Enter a valid pincode to fetch location.');
      return;
    }

    const payload = new FormData();
    payload.append('ProfessionalID', formData.professionalId);
    payload.append('EmailID', formData.emailId);
    payload.append('MobileNumber', formData.mobileNumber);

    if (formData.password) payload.append('Password', formData.password);

    payload.append('IdProfessionType', formData.profession);
    payload.append('ProfessionalName', formData.name);
    payload.append('IdProfessionalDegree', formData.qualification);
    payload.append('Address', formData.address);
    payload.append('IdDistrict', idDistrict);
    payload.append('Pincode', formData.pincode);
    payload.append('Specialization', formData.specialization || '');
    payload.append('RegistrationNumber', formData.registrationNumber || '');
    payload.append('DateOfBirth', formData.dateOfBirth);
    payload.append('Gender', formData.gender);
    payload.append('YearsOfExperience', formData.experience);
    payload.append('ExperienceDetails', formData.experienceDetails);
    payload.append('IsSuspended', false);

    // Append only if new files selected (keeps old files on backend)
    if (cvFile) payload.append('BioDataFile', cvFile);
    if (photoFile) payload.append('PhotoFile', photoFile);
    if (registrationCertificateFile)
      payload.append('RegistrationCertificateFile', registrationCertificateFile);

    try {
      setIsSubmitting(true);
      await client.put('/api/healthcare-professionals', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Profile updated successfully!');
      setIsEditMode(false);

      // reset password fields after save
      setFormData((prev) => ({ ...prev, password: '', reenterPassword: '' }));

      // refresh to re-bind updated photo/files from API
      setCvFile(null);
      setPhotoFile(null);
      setRegistrationCertificateFile(null);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Profile update failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      setIsEditMode(false);
      setCvFile(null);
      setPhotoFile(null);
      setRegistrationCertificateFile(null);
      setFormData((prev) => ({ ...prev, password: '', reenterPassword: '' }));
      await loadData();
    }
  };

  const handleMenuClick = (menuItem) => {
    if (menuItem === 'modify-profile') setIsEditMode(true);
    else if (menuItem === 'opportunities') navigate('/professional-dashboard');
    else if (menuItem === 'applied') navigate('/professional-dashboard');
  };

  return (
    <div className="App">
      <DashboardHeader userType="professional" userName={formData.name} onMenuClick={handleMenuClick} />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-12">
              <div className="login-card">
                <div className="login-header d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="login-title">Professional Profile</h2>
                    <p className="login-subtitle">
                      {isEditMode
                        ? 'Update your profile information below.'
                        : 'View your profile information. Click "Edit Profile" to make changes.'}
                    </p>
                  </div>

                  {!isEditMode && (
                    <button type="button" className="btn btn-primary mt-3" onClick={() => setIsEditMode(true)}>
                      Edit Profile
                    </button>
                  )}
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="row">

                    {/* PHOTO SECTION (bind + edit upload) */}
                    <div className="col-12 p-2">
                      <div className="photo-upload-container">
                        <div className="photo-preview-wrapper">
                          {photoPreview ? (
                            <img src={photoPreview} alt="Profile" className="photo-preview-circle" />
                          ) : (
                            <div className="photo-placeholder-circle">
                              <FaCamera className="photo-placeholder-icon" />
                            </div>
                          )}

                          {isEditMode && (
                            <>
                              <label htmlFor="photoFile" className="photo-upload-overlay">
                                <FaCamera className="camera-icon" />
                              </label>
                              <input
                                type="file"
                                className="photo-file-input"
                                id="photoFile"
                                name="photoFile"
                                accept=".jpg,.jpeg,.png"
                                onChange={(e) => handleFileChange(e, 'Photo')}
                                disabled={isLoading}
                              />
                            </>
                          )}
                        </div>

                        {isEditMode && (
                          <>
                            {photoFile && (
                              <small className="form-text text-success d-block mt-2 text-center">
                                ✓ {photoFile.name} ({formatFileSize(photoFile.size)})
                              </small>
                            )}
                            <small className="form-text text-muted d-block mt-1 text-center">
                              JPG, JPEG, PNG (Max 5MB)
                            </small>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Your Profession */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="profession" className="form-label">
                        Your Profession <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="profession"
                        name="profession"
                        value={formData.profession}
                        onChange={handleChange}
                        disabled={!isEditMode || isLoading}
                        required
                      >
                        <option value="">Select Profession</option>
                        {professions.map((p) => (
                          <option key={p.idProfessionType} value={p.idProfessionType}>
                            {p.professionTypeName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Name */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="name" className="form-label">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        maxLength="60"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                    </div>

                    {/* Gender */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="gender" className="form-label">
                        Gender <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        disabled={!isEditMode || isLoading}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Date of Birth */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="dateOfBirth" className="form-label">
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="dateOfBirth"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        max={new Date().toISOString().split('T')[0]}
                        disabled={!isEditMode || isLoading}
                        required
                      />
                    </div>

                    {/* Address */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="address" className="form-label">
                        Address <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="address"
                        name="address"
                        rows="3"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter your address"
                        maxLength="200"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                      <small className="form-text text-muted">
                        <span id="addressCount">{addressCount}</span>/200 characters
                      </small>
                    </div>

                    {/* Pincode */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="pincode" className="form-label">
                        Pincode <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control w-25"
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          onBlur={validatePincode}
                          placeholder="Pincode"
                          maxLength="6"
                          pattern="[0-9]{6}"
                          disabled={!isEditMode || isLoading}
                          required
                        />
                        <input
                          type="text"
                          className="form-control w-75 form-control-display-only"
                          id="location"
                          name="location"
                          value={location}
                          placeholder="Location"
                          readOnly
                          disabled
                          tabIndex={-1}
                          aria-readonly="true"
                        />
                      </div>
                      {pincodeError && <small className="form-text text-danger">{pincodeError}</small>}
                      {isValidatingPincode && (
                        <small className="form-text text-muted">Validating pincode...</small>
                      )}
                    </div>

                    {/* Qualification */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="qualification" className="form-label">
                        Qualification <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="qualification"
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        disabled={!isEditMode || isQualificationDisabled || isLoading}
                        required
                      >
                        <option value="">Select Qualification</option>
                        {qualificationOptions.map((qual) => (
                          <option key={qual.id} value={qual.id}>
                            {qual.name}
                          </option>
                        ))}
                      </select>
                      {isQualificationDisabled && (
                        <small className="form-text text-muted">Please select your profession first</small>
                      )}
                    </div>

                    {/* Specialization */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="specialization" className="form-label">
                        Specialization
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="specialization"
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleChange}
                        placeholder="Enter your specialization"
                        maxLength="100"
                        disabled={!isEditMode || isLoading}
                      />
                    </div>

                    {/* Registration Number */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="registrationNumber" className="form-label">
                        Registration Number
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="registrationNumber"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                        placeholder="Enter registration number"
                        maxLength="30"
                        disabled={!isEditMode || isLoading}
                      />
                    </div>

                    {/* CV Upload + Existing CV */}
                    <div className="col-md-6 p-2">
                      <label className="form-label">
                        CV (Resume) <span className="text-danger">*</span>
                      </label>

                      {!isEditMode ? (
                        existingFiles.cvBytes ? (
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openBase64InNewTab(existingFiles.cvBytes, existingFiles.cvName || 'CV')}
                            >
                              <FaEye className="me-1" />
                              View CV
                            </button>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-primary text-decoration-none"
                              onClick={() => downloadBase64File(existingFiles.cvBytes, existingFiles.cvName)}
                            >
                              <FaFileAlt className="me-1" />
                              <span className="file-name-truncate" title={existingFiles.cvname || 'Download CV'}>
                                {existingFiles.cvName || 'Download CV'}
                              </span>
                            </button>
                          </div>
                        ) : (
                          <div className="text-muted">-</div>
                        )
                      ) : (
                        <>
                          <input
                            type="file"
                            className="form-control"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'CV')}
                            disabled={isLoading}
                          />
                          {cvFile ? (
                            <small className="form-text text-success d-block mt-1">
                              ✓ Selected: {cvFile.name} ({formatFileSize(cvFile.size)})
                            </small>
                          ) : existingFiles.cvName ? (
                            <small className="form-text text-muted d-block mt-1">
                              Current: {existingFiles.cvName} (upload new to replace)
                            </small>
                          ) : null}
                          <small className="form-text text-muted">
                            Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)
                          </small>
                        </>
                      )}
                    </div>

                    {/* Registration Certificate Upload + Existing */}
                    <div className="col-md-6 p-2">
                      <label className="form-label">
                        Registration Certificate <span className="text-danger">*</span>
                      </label>

                      {!isEditMode ? (
                        existingFiles.certBytes ? (
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openBase64InNewTab(existingFiles.certBytes, existingFiles.certName || 'Certificate')}
                            >
                              <FaEye className="me-1" />
                              View Certificate
                            </button>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-primary text-decoration-none"
                              onClick={() =>
                                downloadBase64File(existingFiles.certBytes, existingFiles.certName)
                              }
                            >
                              <FaFileAlt className="me-1" />

                              <span className="file-name-truncate" title={existingFiles.certName || 'Download Certificate'}>
                                {existingFiles.certName || 'Download Certificate'}
                              </span>
                            </button>
                          </div>
                        ) : (
                          <div className="text-muted">-</div>
                        )
                      ) : (
                        <>
                          <input
                            type="file"
                            className="form-control"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'Registration Certificate')}
                            disabled={isLoading}
                          />
                          {registrationCertificateFile ? (
                            <small className="form-text text-success d-block mt-1">
                              ✓ Selected: {registrationCertificateFile.name} (
                              {formatFileSize(registrationCertificateFile.size)})
                            </small>
                          ) : existingFiles.certName ? (
                            <small className="form-text text-muted d-block mt-1">
                              Current: {existingFiles.certName} (upload new to replace)
                            </small>
                          ) : null}
                          <small className="form-text text-muted">
                            Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)
                          </small>
                        </>
                      )}
                    </div>

                    {/* Experience */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="experience" className="form-label">
                        Experience (In Years) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        placeholder="Enter years of experience"
                        min="0"
                        max="70"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                    </div>

                    {/* Experience Details */}
                    <div className="col-md-12 p-2">
                      <label htmlFor="experienceDetails" className="form-label">
                        Experience Details <span className="text-danger">*</span>
                      </label>

                      {isEditMode ? (
                        <ReactQuill
                          theme="snow"
                          value={formData.experienceDetails}
                          onChange={handleExperienceDetailsChange}
                          placeholder="Enter your experience details"
                          className="experience-details-editor"
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ indent: '-1' }, { indent: '+1' }],
                              ['link'],
                              ['clean']
                            ]
                          }}
                          formats={[
                            'header',
                            'bold',
                            'italic',
                            'underline',
                            'strike',
                            'list',
                            'bullet',
                            'indent',
                            'link'
                          ]}
                          style={{ minHeight: '100px', marginBottom: '10px' }}
                        />
                      ) : (
                        <div
                          className="form-control"
                          style={{
                            minHeight: '100px',
                            padding: '12px 15px',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            backgroundColor: '#e9ecef',
                            cursor: 'not-allowed'
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              formData.experienceDetails ||
                              '<p style="color: #6c757d;">No experience details provided.</p>'
                          }}
                        />
                      )}

                      <small className="form-text text-muted">
                        <span id="experienceCount">{experienceCount}</span>/8000 characters
                      </small>
                    </div>

                    {/* Mobile */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="mobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        onBlur={() => {
                          if (!isEditMode) return;
                          const cur = (formData.mobileNumber || '').trim();
                          const orig = (initialContact.mobileNumber || '').trim();
                          if (cur.length === 10 && cur !== orig) validateContact('mobile');
                        }}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                        pattern="[0-9]{10}"
                        disabled={!isEditMode || isLoading}
                        required
                      />
                      {mobileError && <small className="form-text text-danger">{mobileError}</small>}
                    </div>

                    {/* Email */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="emailId" className="form-label">
                        Email ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-display-only"
                        id="emailId"
                        name="emailId"
                        value={formData.emailId}
                        onChange={handleChange}
                        onBlur={() => {
                          if (!isEditMode) return;
                          const cur = (formData.emailId || '').trim().toLowerCase();
                          const orig = (initialContact.emailId || '').trim().toLowerCase();
                          const emailValid = /^\S+@\S+\.\S+$/.test(cur);
                          if (emailValid && cur !== orig) validateContact('email');
                        }}
                        placeholder="Enter your email ID"
                        maxLength="50"
                        readOnly
                        disabled
                        required
                      />
                      {emailError && <small className="form-text text-danger">{emailError}</small>}

                    </div>

                    {/* Passwords (only edit mode) */}
                    {isEditMode && (
                      <>
                        <div className="col-md-6 p-2">
                          <label htmlFor="password" className="form-label">
                            New Password{' '}
                            <small className="text-muted">(Leave blank to keep current password)</small>
                          </label>
                          <div className="input-group">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              id="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              placeholder="Enter new password (6-20 characters)"
                              minLength="6"
                              disabled={isLoading}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              disabled={isLoading}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </div>

                        <div className="col-md-6 p-2">
                          <label htmlFor="reenterPassword" className="form-label">
                            Re-enter New Password
                          </label>
                          <div className="input-group">
                            <input
                              type={showReenterPassword ? 'text' : 'password'}
                              className={`form-control ${formData.reenterPassword && !passwordsMatch
                                ? 'is-invalid'
                                : formData.reenterPassword && passwordsMatch
                                  ? 'is-valid'
                                  : ''
                                }`}
                              id="reenterPassword"
                              name="reenterPassword"
                              value={formData.reenterPassword}
                              onChange={handleChange}
                              placeholder="Re-enter new password"
                              minLength="6"
                              disabled={isLoading}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowReenterPassword(!showReenterPassword)}
                              disabled={isLoading}
                            >
                              {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          {formData.reenterPassword && !passwordsMatch && (
                            <div className="invalid-feedback d-block">Passwords do not match</div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Buttons */}
                    {isEditMode && (
                      <div className="text-center mt-4">
                        <button
                          type="submit"
                          className="btn login-submit-btn px-5 me-2"
                          disabled={isSubmitting || isValidatingPincode || isValidatingContact}
                        >
                          {isSubmitting ? 'Updating...' : 'Update Profile'}
                        </button>
                        <button
                          type="button"
                          className="btn cancel-btn px-5"
                          onClick={handleCancel}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ProfessionalProfile;
